import { APP_ROUTES } from '../constants';
import { getJWTSub } from '../utils/auth';
import { CONFIG } from '../utils/config';

// Global flag to track if Pendo has been initialized
let pendoInitialized = false;

/**
 * Initialize Pendo agent (only once)
 * This loads the Pendo script and sets up the global pendo object
 */
const loadPendoScript = () => {
  if (pendoInitialized || window.pendo) {
    console.log('Pendo already initialized');
    return;
  }

  const apiKey = CONFIG.REACT_APP_PENDO;
  if (!apiKey) {
    console.warn('Pendo API key not configured');
    return;
  }

  console.log('Loading Pendo script...');
  
  (function (p, e, n, d, o) {
    var v, w, x, y, z;
    o = p[d] = p[d] || {};
    o._q = o._q || [];

    v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
    for (w = 0, x = v.length; w < x; ++w) {
      (function (m) {
        o[m] =
          o[m] ||
          function () {
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
      })(v[w]);
    }
    y = e.createElement(n);
    y.async = true;
    y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
    z = e.getElementsByTagName(n)[0];
    z.parentNode.insertBefore(y, z);
  })(window, document, 'script', 'pendo');

  pendoInitialized = true;
};

/**
 * Initialize Pendo with user and account data
 * Should be called once when user authentication is established
 */
export const initializePendo = (visitor, account) => {
  // Load script if not already loaded
  loadPendoScript();
  
  if (!window.pendo) {
    console.warn('Pendo not available');
    return;
  }

  // Initialize Pendo
  window.pendo.initialize({ visitor, account });
};

/**
 * Update Pendo options when context changes (e.g., navigating between MFEs)
 * This allows tracking which MFE/page the user is currently using
 */
export const updatePendoOptions = (visitor, account) => {
  if (!window.pendo) {
    console.warn('Pendo not initialized, cannot update options');
    return;
  }

  // Update Pendo options
  window.pendo.updateOptions({ visitor, account });
};

/**
 * Initialize or update Pendo based on current state
 * This is the main function to call from MFEs and the shell
 */
export const ensurePendoReady = (visitor, account) => {
  if (!window.pendo) {
    initializePendo(visitor, account);
    return;
  }
  updatePendoOptions(visitor, account);
};

/**
 * Get the current route-based source name for Pendo tracking
 */
export const getSourceFromRoute = (pathname) => {
  return APP_ROUTES[pathname];
};

// Store reference to the handler for cleanup
let pendoRoutingHandler = null;

/**
 * Build base visitor data with common fields
 */
const buildBaseVisitorData = (gaClientId) => {
  const source = getSourceFromRoute(window.location.pathname);
  return {
    ...(gaClientId && { gaClientId }),
    ...(source && { source })
  };
};

/**
 * Build visitor data for authenticated users
 */
const buildAuthenticatedVisitorData = (gaClientId) => {
  const user = getJWTSub();
  return {
    ...(user?.aoid && { aoId: user.aoid }),
    ...buildBaseVisitorData(gaClientId)
  };
};

/**
 * Build account data from user context
 */
const buildAccountData = (userContext) => {
  const { adpWebID, userAppStoreProfiles = {} } = userContext;
  const { businessTypeCodes = [] } = userAppStoreProfiles;
  const organizationName = userContext?.userInfo?.userInfo?.organizationName;

  return {
    ...(adpWebID && { adpWebID }),
    ...(businessTypeCodes.length && { businessType: businessTypeCodes.join() }),
    env: CONFIG.REACT_APP_STAGE,
    ...(organizationName && { client: organizationName })
  };
};

/**
 * Handle Pendo tracking based on authentication state and user context
 */
export const handlePendoTracking = ({ authenticated, userContext, gaClientId }) => {
  // Non-logged in user
  if (!authenticated) {
    const visitorData = {
      ...buildBaseVisitorData(gaClientId),
      env: CONFIG.REACT_APP_STAGE
    };
    ensurePendoReady(visitorData, {});
    return;
  }

  // Logged in user with valid context
  if (userContext?.userInfo) {
    const visitorData = buildAuthenticatedVisitorData(gaClientId);
    const accountData = buildAccountData(userContext);
    ensurePendoReady(visitorData, accountData);
  }
};

/**
 * Load Pendo tracking with routing event listener
 */
export const loadPendoTracking = ({ authenticated, userContext, gaClientId }) => {
  // Initial tracking call
  handlePendoTracking({ authenticated, userContext, gaClientId });

  // Create handler that captures current params
  pendoRoutingHandler = () => {
    handlePendoTracking({ authenticated, userContext, gaClientId });
  };

  // Listen to Single-SPA routing events
  window.addEventListener('single-spa:routing-event', pendoRoutingHandler);
};

/**
 * Unload Pendo tracking and remove routing event listener
 */
export const unloadPendoTracking = () => {
  if (pendoRoutingHandler) {
    window.removeEventListener('single-spa:routing-event', pendoRoutingHandler);
    pendoRoutingHandler = null;
  }
};